import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  GeneralPageConfigModel,
  PageConfigName,
  UserBackOfficeSettingsModel
} from '@bo-client/shared/models/userBackOfficeSettings';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, shareReplay, startWith } from 'rxjs/operators';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';

@Component({
  selector: 'app-edit-personalization-modal',
  templateUrl: './edit-personalization-modal.component.html',
  styleUrls: ['./edit-personalization-modal.component.scss']
})
export class EditPersonalizationModalComponent {
  private dialogRef = inject(
    MatDialogRef<
      EditPersonalizationModalComponent,
      null | Pick<UserBackOfficeSettingsModel, 'generalPageConfig' | 'pageConfigs'>
    >
  );
  private formBuilder = inject(FormBuilder);
  private userConfigService = inject(UserConfigService);
  private authService = inject(AuthorizationService);

  pageConfigsForm$: Observable<PageConfigsForm>;
  generalPagePreferencesForm$: Observable<TypedForm<GeneralPageConfigModel>>;
  allPagesV2Selected: boolean;
  permissions$: Observable<Permissions>;

  constructor() {
    this.pageConfigsForm$ = this.userConfigService.userConfigs$.pipe(
      takeUntilDestroyed(),
      map((userConfigs) => userConfigs?.pageConfigs || {}),
      map((config) => {
        const {
          'order-request': orderRequest,
          'admin-installer-pass': installerPass,
          'commissioning-request': commissioningRequest,
          'grid-registration': gridRegistration,
          'feeder-contract': feederContract
        } = config;
        return this.formBuilder.group({
          'order-request': this.formBuilder.group({
            pageVersion: [orderRequest?.pageVersion || null]
          }),
          'admin-installer-pass': this.formBuilder.group({
            pageVersion: [installerPass?.pageVersion || null]
          }),
          'commissioning-request': this.formBuilder.group({
            pageVersion: [commissioningRequest?.pageVersion || null]
          }),
          'grid-registration': this.formBuilder.group({
            pageVersion: [gridRegistration?.pageVersion || null]
          }),
          'feeder-contract': this.formBuilder.group({
            pageVersion: [feederContract?.pageVersion || null]
          })
        } as { [K in PageConfigName]: FormGroup<{ pageVersion: FormControl<string | null> }> });
      }),
      shareReplay(1)
    );

    this.generalPagePreferencesForm$ = this.userConfigService.userConfigs$.pipe(
      takeUntilDestroyed(),
      map((userConfigs) => userConfigs?.generalPageConfig || new GeneralPageConfigModel()),
      map((generalPageConfig) => {
        return this.formBuilder.group({
          listSize: [
            generalPageConfig.listSize || null,
            [Validators.required, Validators.min(10), Validators.max(50)]
          ]
        });
      }),
      shareReplay(1)
    );

    this.permissions$ = this.authService.isInstaller$.pipe(
      takeUntilDestroyed(),
      combineLatestWith(
        this.authService.hasPermission$('VIEW_HA_LIST'),
        this.authService.hasPermission$('VIEW_INSTALLER_PASS_LIST'),
        this.authService.hasPermission$('VIEW_COMMISSIONING_LIST_ADMIN'),
        this.authService.hasPermission$('VIEW_GRID_REGISTRATION_LIST_ADMIN'),
        this.authService.hasOneOfThisRoles$([
          'ROLE_VIEWER_FEEDERCONTRACTS',
          'ROLE_KEYUSER_FEEDERCONTRACTS',
          'ROLE_EDITOR_FEEDERCONTRACTS',
          'ROLE_SUPERADMIN'
        ])
      ),
      map(([isInstaller, orderRequest, installerPass, commissioning, gridRegistration, feederContract]) => ({
        installer: isInstaller,
        orderRequest,
        installerPass,
        commissioning,
        gridRegistration,
        feederContract
      })),
      shareReplay(1)
    );
  }

  onToggleAllPages(checked: boolean, pageConfigsForm: PageConfigsForm) {
    const newPageVersion = checked ? 'v2' : 'v1';
    Object.keys(pageConfigsForm.controls).forEach((key) => {
      const control = pageConfigsForm.controls[key as PageConfigName];
      control.setValue({ pageVersion: newPageVersion });
      control.markAsTouched();
    });
  }

  isAllPagesV2Selected$ = (form: PageConfigsForm): Observable<boolean> => {
    return form.valueChanges.pipe(
      startWith(form.value),
      map((value) => Object.values(value).every((config) => config.pageVersion === 'v2'))
    );
  };

  onCancel = () => {
    this.dialogRef.close(null);
  };

  onConfirm = (pageConfigsForm: FormGroup, generalPagePreferencesForm: FormGroup) => {
    if (pageConfigsForm.invalid || generalPagePreferencesForm.invalid) {
      pageConfigsForm.markAllAsTouched();
      generalPagePreferencesForm.markAllAsTouched();
      return;
    }
    this.dialogRef.close({
      pageConfigs: pageConfigsForm.value,
      generalPageConfig: generalPagePreferencesForm.value
    });
  };
}

type PageConfigsForm = FormGroup<{
  [K in PageConfigName]: FormGroup<{
    pageVersion: FormControl<string | null>;
  }>;
}>;

type Permissions = {
  installer: boolean;
  orderRequest: boolean;
  installerPass: boolean;
  commissioning: boolean;
  gridRegistration: boolean;
  feederContract: boolean;
};
